import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { designService } from '../services/api'
import LoadingSpinner from './LoadingSpinner'

export default function CSVUploader({ onUploadComplete }) {
  const navigate = useNavigate()
  const [file, setFile] = useState(null)
  const [templateMode, setTemplateMode] = useState('auto')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(null)
  const [csvPreview, setCsvPreview] = useState(null)
  const [csvError, setCsvError] = useState(null)
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [s3Images, setS3Images] = useState([])
  const [matchedImages, setMatchedImages] = useState({})

  const matchImageFromS3 = (filename, images) => {
    if (!filename || !images.length) return null

    // Case-insensitive matching - extract just the product name without extension
    const searchName = filename.toLowerCase().split('.')[0]

    // First try exact match
    for (let img of images) {
      const s3Name = img.filename.toLowerCase().split('.')[0]
      if (s3Name === searchName) {
        return img
      }
    }

    // If no exact match, try partial/fuzzy matching
    // Check if S3 filename contains the search term or vice versa
    let bestMatch = null
    let bestScore = 0

    for (let img of images) {
      const s3Name = img.filename.toLowerCase().split('.')[0]

      // Score 1: If one contains the other (banana matches bananas)
      if (s3Name.includes(searchName) || searchName.includes(s3Name)) {
        const score = Math.max(searchName.length, s3Name.length) /
                      Math.min(searchName.length, s3Name.length)
        if (score > bestScore && score < 1.5) {
          bestScore = score
          bestMatch = img
        }
      }

      // Score 2: Check similarity using simple character matching
      const similarity = calculateSimilarity(searchName, s3Name)
      if (similarity > bestScore && similarity > 0.7) {
        bestScore = similarity
        bestMatch = img
      }
    }

    return bestMatch
  }

  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2
    const shorter = str1.length > str2.length ? str2 : str1

    const editDistance = getEditDistance(longer.toLowerCase(), shorter.toLowerCase())
    return (longer.length - editDistance) / longer.length
  }

  const getEditDistance = (s1, s2) => {
    const costs = []
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j
        } else if (j > 0) {
          let newValue = costs[j - 1]
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1
          }
          costs[j - 1] = lastValue
          lastValue = newValue
        }
      }
      if (i > 0) costs[s2.length] = lastValue
    }
    return costs[s2.length]
  }

  const loadS3Images = async () => {
    try {
      const response = await designService.listImages()
      const imageList = Array.isArray(response.data) ? response.data : response.data?.data || []
      setS3Images(imageList)
      console.log('Loaded S3 images:', imageList)
      return imageList
    } catch (err) {
      console.error('Error loading S3 images:', err)
      return []
    }
  }

  const parseCSVPreview = async (csvText) => {
    try {
      const lines = csvText.trim().split('\n')
      if (lines.length === 0) {
        setCsvError('CSV file is empty')
        return
      }

      // Parse headers
      const headers = lines[0].split(',').map(h => h.trim())
      const requiredColumns = ['product_name', 'quantity', 'price', 'image_filename']
      const missingColumns = requiredColumns.filter(col => !headers.includes(col))

      if (missingColumns.length > 0) {
        setCsvError(`Missing required columns: ${missingColumns.join(', ')}`)
        setCsvPreview(null)
        return
      }

      // Load S3 images for matching
      const images = await loadS3Images()

      // Parse all rows for preview
      const rows = []
      const matched = {}
      for (let i = 1; i < Math.min(101, lines.length); i++) {
        const values = lines[i].split(',').map(v => v.trim())
        const row = {}
        headers.forEach((header, idx) => {
          row[header] = values[idx] || ''
        })
        rows.push(row)

        // Match image for this row
        if (row.image_filename) {
          const matchedImg = matchImageFromS3(row.image_filename, images)
          matched[i - 1] = matchedImg
        }
      }

      setMatchedImages(matched)
      setCsvPreview({
        headers,
        rows,
        totalRows: lines.length - 1,
        allLines: lines // Store all lines for later processing
      })
      // Select all rows by default
      setSelectedRows(new Set(rows.map((_, idx) => idx)))
      setCsvError(null)
    } catch (err) {
      setCsvError('Error parsing CSV file')
      setCsvPreview(null)
    }
  }

  const handleRowSelect = (rowIdx) => {
    const newSelected = new Set(selectedRows)
    if (newSelected.has(rowIdx)) {
      newSelected.delete(rowIdx)
    } else {
      newSelected.add(rowIdx)
    }
    setSelectedRows(newSelected)
  }

  const handleSelectAllRows = () => {
    if (selectedRows.size === csvPreview.rows.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(csvPreview.rows.map((_, idx) => idx)))
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file')
        setCsvPreview(null)
        return
      }

      // Read and preview the file
      const reader = new FileReader()
      reader.onload = async (event) => {
        const csvText = event.target?.result
        await parseCSVPreview(csvText)
      }
      reader.readAsText(selectedFile)

      setFile(selectedFile)
      setError(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!file) {
      setError('Please select a CSV file')
      return
    }

    if (selectedRows.size === 0) {
      setError('Please select at least one row to generate')
      return
    }

    try {
      setLoading(true)

      // Get selected row count for progress tracking
      const selectedCount = selectedRows.size

      // Create a filtered CSV with only selected rows
      const lines = csvPreview.allLines
      const headerLine = lines[0]

      const selectedRowLines = Array.from(selectedRows)
        .sort((a, b) => a - b)
        .map(idx => lines[idx + 1])

      const filteredCSV = [headerLine, ...selectedRowLines].join('\n')
      const filteredFile = new File([filteredCSV], file.name, { type: 'text/csv' })

      setProgress({ total: selectedCount, current: 0, message: `⏳ Processing ${selectedCount} design(s)...` })

      const response = await designService.bulkUpload(filteredFile, templateMode)
      const result = response.data

      if (!result) {
        setError('No response from server')
        setLoading(false)
        return
      }

      if (result.total_designs === 0) {
        setError('No designs were created. Check CSV format and file content.')
        setLoading(false)
        return
      }

      // Count actual completed (successful_renders is what matters)
      const completed = result.successful_renders || 0
      const total = selectedCount

      setProgress({
        total: total,
        current: completed,
        message: `✅ ${completed} of ${total} designs created!`
      })
      onUploadComplete(result)
      setError(null)

      // Navigate to History page to show the generated designs
      setTimeout(() => {
        navigate('/history')
      }, 1500)
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error uploading CSV'
      setError(errorMsg)
      console.error('Upload error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      })
    } finally {
      setLoading(false)
    }
  }

  const downloadSampleCSV = () => {
    const sampleCSV = `product_name,quantity,price,image_filename,validity_text
Bananas,for 100g,150,bananas.jpeg,Valid from June 11 to June 15 2026
Cabbage,per kg,85,CABBAGE.jpg,Valid from June 11 to June 15 2026
Cucumber,per piece,40,CUCUMBER.jpg,Valid from June 11 to June 15 2026
Ladies Fingers,per 250g,95,LADIES FINGERS.jpg,Valid from June 11 to June 15 2026`

    const blob = new Blob([sampleCSV], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sample-cargills.csv'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  if (loading) {
    return (
      <div className="uploader">
        <LoadingSpinner />
        {progress && (
          <div className="progress-info">
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
              {progress.message || 'Generating designs...'}
            </p>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Creating {progress.total} design(s)
            </p>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
              />
            </div>
            <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {progress.current} of {progress.total} completed
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="uploader">
      <h2>Bulk Upload CSV</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="csv-uploader-layout">
        <form onSubmit={handleSubmit} className="form csv-form">
          <div className="form-section">
            <label>CSV File</label>
            <label className="file-input-label">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                required
                className="file-input"
              />
              <span className="file-name">
                {file ? `✓ ${file.name}` : 'Click to select CSV file'}
              </span>
            </label>
            <p className="helper-text">
              CSV columns: product_name, quantity, price, image_filename, validity_text
            </p>
          </div>

          <div className="form-section">
            <label>Template Mode</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="single"
                  checked={templateMode === 'single'}
                  onChange={(e) => setTemplateMode(e.target.value)}
                />
                Single Template (all items separately)
              </label>
              <label>
                <input
                  type="radio"
                  value="double"
                  checked={templateMode === 'double'}
                  onChange={(e) => setTemplateMode(e.target.value)}
                />
                Double Template (pairs only)
              </label>
              <label>
                <input
                  type="radio"
                  value="auto"
                  checked={templateMode === 'auto'}
                  onChange={(e) => setTemplateMode(e.target.value)}
                />
                Auto Detect (pairs → double, remainder → single)
              </label>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={!file || csvError}>
            Upload & Generate Designs
          </button>
        </form>

        <div className="csv-preview-column">
          {csvError && (
            <div className="csv-error">
              <p>⚠️ {csvError}</p>
            </div>
          )}

          {csvPreview && (
            <div className="csv-preview-section">
              <h3>📋 CSV Preview</h3>
              <p className="preview-info">Showing first {csvPreview.rows.length} of {csvPreview.totalRows} rows</p>
              <div className="csv-table-wrapper">
                <table className="csv-preview-table">
                  <thead>
                    <tr>
                      <th className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedRows.size === csvPreview.rows.length && csvPreview.rows.length > 0}
                          onChange={handleSelectAllRows}
                          title="Select all visible rows"
                        />
                      </th>
                      {csvPreview.headers.map((header, idx) => (
                        <th key={idx}>{header}</th>
                      ))}
                      <th className="image-match-col">Image Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.rows.map((row, rowIdx) => {
                      const matchedImg = matchedImages[rowIdx]
                      return (
                        <tr
                          key={rowIdx}
                          className={selectedRows.has(rowIdx) ? 'selected' : ''}
                          onClick={() => handleRowSelect(rowIdx)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedRows.has(rowIdx)}
                              onChange={() => handleRowSelect(rowIdx)}
                            />
                          </td>
                          {csvPreview.headers.map((header, colIdx) => (
                            <td key={colIdx}>{row[header]}</td>
                          ))}
                          <td className="image-match-col">
                            {matchedImg ? (
                              <img
                                src={matchedImg.url}
                                alt={matchedImg.filename}
                                className="image-thumbnail"
                                title={matchedImg.filename}
                              />
                            ) : (
                              <span className="no-match">❌ No match</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!csvPreview && !csvError && (
            <div className="csv-empty-state">
              <p>📄</p>
              <p>Select a CSV file to preview</p>
              <button
                type="button"
                onClick={downloadSampleCSV}
                className="sample-csv-btn"
              >
                📥 Download Sample CSV
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
